/**
 * Sign-in methods — every way into the account, in one card (design approved
 * 26 Sep 2026): the email (and its Change dialog), the password (add or
 * change, in a dialog), and the connected accounts in SOCIAL_PROVIDERS order —
 * each connected one with its address and Disconnect, the others Connect. The
 * providers are the ones the Clerk instance lets an account connect, plus any
 * already connected (`useConnectedAccounts`); with none, the section is not
 * shown.
 *
 * It replaces three cards that each looked different and showed the same
 * address twice without saying how they relate: the email row now says what
 * the address is for, and the email dialog says which connected account signs
 * in through it.
 */
import { useUser } from '@clerk/clerk-react';
import { Loader2, Lock, Mail } from '@tonnie/assets';
import { SOCIAL_PROVIDER_NAME } from '@tonnie/foundation';
import { useTranslation } from '@tonnie/locale';
import { useAccountPassword } from '@tonnie/sdk';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  SettingsBadge,
  SettingsCard,
  SettingsRow,
  SettingsSubheading,
  SocialProviderMark,
} from '@tonnie/ui';
import { useState } from 'react';
import { toast } from 'sonner';

import { useAccountPortal } from '../portal';
import { useChangeEmailDialog } from './hooks/use-change-email-dialog';
import { useConnectedAccountsCard } from './hooks/use-connected-accounts-card';
import { useSignInProviders } from './hooks/use-sign-in-providers';
import { ChangeEmailDialog } from './ChangeEmailDialog';
import { PasswordDialog } from './PasswordDialog';

export function SignInMethodsCard() {
  const { t } = useTranslation();
  const { user } = useUser();
  const { hasPassword } = useAccountPassword();
  const { names } = useSignInProviders();
  const emailDialog = useChangeEmailDialog();
  // Back here after the provider's consent screen.
  const { securitySettingsPath } = useAccountPortal();
  const connected = useConnectedAccountsCard({ notify: toast, returnUrl: securitySettingsPath });
  const [passwordOpen, setPasswordOpen] = useState(false);

  const email = user?.primaryEmailAddress?.emailAddress ?? '';
  const removingName = connected.removing ? SOCIAL_PROVIDER_NAME[connected.removing.provider] : '';

  return (
    <SettingsCard
      title={t('auth.security.methods.title')}
      description={t('auth.security.methods.description')}
      data-testid="settings-sign-in-methods"
    >
      <SettingsRow
        icon={<Mail className="h-[18px] w-[18px]" />}
        title={t('auth.security.email.label')}
        badge={<SettingsBadge>{t('auth.security.email.badge')}</SettingsBadge>}
        detail={<span data-testid="settings-email-current">{email}</span>}
        action={
          <Button
            variant="outline"
            size="sm"
            onClick={emailDialog.open}
            data-testid="settings-email-change"
          >
            {t('auth.security.email.change')}
          </Button>
        }
        data-testid="settings-email-row"
      />
      <SettingsRow
        icon={<Lock className="h-[18px] w-[18px]" />}
        title={t('auth.security.password.label')}
        detail={
          hasPassword
            ? t('auth.security.password.set')
            : names
              ? t('auth.security.password.notSetWith', { providers: names })
              : t('auth.security.password.notSet')
        }
        action={
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPasswordOpen(true)}
            data-testid="settings-password-open"
          >
            {hasPassword ? t('auth.security.password.change') : t('auth.security.password.add')}
          </Button>
        }
        data-testid="settings-password-row"
      />
      {connected.providers.length > 0 && (
        <>
          <SettingsSubheading>{t('auth.security.connected.heading')}</SettingsSubheading>
          {connected.error && (
            <li role="alert" className="px-6 pb-2 text-sm text-destructive-accessible">
              {connected.error}
            </li>
          )}
          {connected.providers.map((provider) => {
            const account = connected.accounts.find((a) => a.provider === provider);
            const busy = connected.busy === provider;
            return (
              <SettingsRow
                key={provider}
                iconTone="neutral"
                icon={<SocialProviderMark provider={provider} size={18} />}
                title={SOCIAL_PROVIDER_NAME[provider]}
                badge={
                  account ? (
                    <SettingsBadge tone="success">
                      {t('auth.security.connected.connected')}
                    </SettingsBadge>
                  ) : undefined
                }
                detail={account ? (account.email ?? '') : t('auth.security.connected.notConnected')}
                className="py-3"
                action={
                  account ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive-accessible hover:bg-destructive/10 hover:text-destructive-accessible"
                      disabled={connected.busy !== null}
                      onClick={() => connected.askToDisconnect(account)}
                      data-testid={`settings-disconnect-${provider}`}
                    >
                      {busy && (
                        <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden="true" />
                      )}
                      {t('auth.connected.disconnect')}
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={connected.busy !== null}
                      aria-busy={busy}
                      aria-label={t('auth.connected.connect', {
                        provider: SOCIAL_PROVIDER_NAME[provider],
                      })}
                      onClick={() => void connected.onConnect(provider)}
                      data-testid={`settings-connect-${provider}`}
                    >
                      {busy && (
                        <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden="true" />
                      )}
                      {t('auth.security.connected.connect')}
                    </Button>
                  )
                }
                data-testid={`settings-connected-${provider}`}
              />
            );
          })}
        </>
      )}

      <ChangeEmailDialog dialog={emailDialog} />
      <PasswordDialog
        open={passwordOpen}
        onOpenChange={setPasswordOpen}
        hasPassword={hasPassword}
        email={email}
      />
      <AlertDialog
        open={connected.removing !== null}
        onOpenChange={(open) => !open && connected.cancelDisconnect()}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('auth.connected.disconnectTitle', { provider: removingName })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('auth.connected.disconnectDesc', { provider: removingName })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={connected.onConfirmDisconnect}
              data-testid="settings-disconnect-confirm"
            >
              {t('auth.connected.disconnect')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SettingsCard>
  );
}
